import React, { useState, useEffect } from 'react';
import { Briefcase, ExternalLink, Trash2, ArrowLeft, Filter, Sparkles, FileDown, Camera, Download, Wand } from 'lucide-react';
import { getAllJobs, updateJob, deleteJob, attachResumeSnapshot, clearResumeSnapshot, runFullCleanup, verifyTracker } from '../jobStorage';
import { toJsonResume } from '../jsonResume.js';

// Markdown export inspired by career-ops's `data/applications.md` — a single
// git-friendly file holding every application + status. Gives users a portable
// backup and an escape hatch so the tracker isn't a one-way Firestore lock-in.
const exportToMarkdown = (jobs) => {
    const today = new Date().toISOString().slice(0, 10);
    const byStatus = jobs.reduce((acc, j) => {
        const s = j.status || 'Uncategorized';
        if (!acc[s]) acc[s] = [];
        acc[s].push(j);
        return acc;
    }, {});

    const order = ['Interested', 'Applied', 'Interview', 'Offer', 'Rejected', 'Uncategorized'];
    const sections = order.filter(s => byStatus[s]?.length).map(status => {
        const lines = [`## ${status} (${byStatus[status].length})`, ''];
        for (const j of byStatus[status]) {
            const added = j.dateAdded ? new Date(j.dateAdded).toISOString().slice(0, 10) : '';
            const match = j.matchScore ? ` — ${j.matchScore}% match` : '';
            lines.push(`### ${j.title || '(Untitled role)'} @ ${j.company || '(Unknown company)'}`);
            lines.push(`- **Added:** ${added}${match}`);
            if (j.link) lines.push(`- **Posting:** ${j.link}`);
            if (j.analysis?.matchingSkills?.length) {
                lines.push(`- **Matching skills:** ${j.analysis.matchingSkills.join(', ')}`);
            }
            if (j.analysis?.missingSkills?.length) {
                lines.push(`- **Missing skills:** ${j.analysis.missingSkills.join(', ')}`);
            }
            if (j.notes) {
                lines.push('', j.notes.trim());
            }
            lines.push('');
        }
        return lines.join('\n');
    });

    const header = [
        `# Job Applications — ${today}`,
        '',
        `Exported from Resume Architect — ${jobs.length} entries total.`,
        '',
        '---',
        ''
    ].join('\n');

    return header + sections.join('\n---\n\n');
};

const STATUS_OPTIONS = ['Interested', 'Applied', 'Interview', 'Rejected', 'Offer'];
const STATUS_COLORS = {
    'Interested': 'bg-blue-100 text-blue-700',
    'Applied': 'bg-yellow-100 text-yellow-700',
    'Interview': 'bg-purple-100 text-purple-700',
    'Rejected': 'bg-red-100 text-red-700',
    'Offer': 'bg-green-100 text-green-700'
};

const ApplicationTracker = ({ onBack, onAnalyzeNew, onTailor, onEdit, currentResumeData, currentTemplate, showNotification }) => {
    const [jobs, setJobs] = useState([]);
    const [filterStatus, setFilterStatus] = useState('All');
    const [cleanupReport, setCleanupReport] = useState(null);
    const [health, setHealth] = useState(null);

    useEffect(() => {
        loadJobs();
    }, []);

    const loadJobs = () => {
        setJobs(getAllJobs());
        setHealth(verifyTracker());
    };

    const handleCleanup = () => {
        const report = runFullCleanup();
        setCleanupReport(report);
        loadJobs();
        const dropped = report.deduped.removed;
        const normalized = report.normalized.changed;
        if (dropped === 0 && normalized === 0 && report.verifyBefore.issues.length === 0) {
            showNotification?.('Tracker is already clean ✓');
        } else {
            showNotification?.(`Cleaned: ${dropped} duplicate${dropped === 1 ? '' : 's'} merged, ${normalized} status${normalized === 1 ? '' : 'es'} normalized.`);
        }
    };

    const handleStatusChange = (jobId, newStatus) => {
        updateJob(jobId, { status: newStatus });
        loadJobs();
    };

    const handleDelete = (jobId) => {
        if (window.confirm('Are you sure you want to delete this job?')) {
            deleteJob(jobId);
            loadJobs();
        }
    };

    const filteredJobs = filterStatus === 'All'
        ? jobs
        : jobs.filter(j => j.status === filterStatus);

    const handleSnapshot = (jobId) => {
        if (!currentResumeData) {
            showNotification?.('No active resume to snapshot.');
            return;
        }
        attachResumeSnapshot(jobId, currentResumeData, currentTemplate);
        loadJobs();
        showNotification?.('Resume snapshot attached to this application.');
    };

    const handleDownloadSnapshot = (job) => {
        if (!job.snapshot) return;
        const payload = toJsonResume(job.snapshot.resumeData);
        const slug = `${(job.company || 'company').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-${(job.title || 'role').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;
        const date = job.snapshot.savedAt ? job.snapshot.savedAt.slice(0, 10) : new Date().toISOString().slice(0, 10);
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `cv-${slug}-${date}.json`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    const handleClearSnapshot = (jobId) => {
        if (!window.confirm('Remove the saved resume snapshot for this application?')) return;
        clearResumeSnapshot(jobId);
        loadJobs();
        showNotification?.('Snapshot removed.');
    };

    const handleExportMarkdown = () => {
        if (jobs.length === 0) return;
        const md = exportToMarkdown(jobs);
        const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `applications-${new Date().toISOString().slice(0, 10)}.md`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="min-h-screen bg-slate-50 p-6">
            <div className="max-w-6xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                    <button
                        onClick={onBack}
                        className="flex items-center gap-2 text-slate-600 hover:text-slate-800"
                    >
                        <ArrowLeft size={20} />
                        Back
                    </button>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleCleanup}
                            disabled={jobs.length === 0}
                            title="Dedup, normalize statuses, verify integrity"
                            className="flex items-center gap-2 text-purple-600 hover:bg-purple-50 disabled:opacity-50 disabled:hover:bg-transparent px-3 py-2 rounded-lg font-medium transition-colors text-sm"
                        >
                            <Wand size={16} />
                            <span className="hidden sm:inline">Cleanup</span>
                        </button>
                        <button
                            onClick={handleExportMarkdown}
                            disabled={jobs.length === 0}
                            title="Export tracker as Markdown — git-friendly portable backup"
                            className="flex items-center gap-2 text-slate-600 hover:bg-slate-100 disabled:opacity-50 disabled:hover:bg-transparent px-3 py-2 rounded-lg font-medium transition-colors text-sm"
                        >
                            <FileDown size={16} />
                            <span className="hidden sm:inline">Export .md</span>
                        </button>
                        <button
                            onClick={onAnalyzeNew}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                        >
                            Analyze New Job
                        </button>
                    </div>
                </div>

                {/* Health banner — only shown when there are actual issues */}
                {health && !health.healthy && (
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900 flex items-center gap-2">
                        <span className="font-bold">Tracker needs cleanup:</span>
                        {health.duplicateGroups > 0 && <span>{health.duplicateGroups} duplicate group{health.duplicateGroups === 1 ? '' : 's'}</span>}
                        {health.issues.length > 0 && <span>{health.duplicateGroups > 0 ? ', ' : ''}{health.issues.length} other issue{health.issues.length === 1 ? '' : 's'}</span>}
                        <button onClick={handleCleanup} className="ml-auto text-xs font-bold text-amber-700 hover:underline">
                            Fix automatically →
                        </button>
                    </div>
                )}

                {/* Last cleanup report */}
                {cleanupReport && (
                    <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-900 flex items-center gap-3">
                        <span className="font-bold">Cleanup complete:</span>
                        <span>{cleanupReport.deduped.removed} dup{cleanupReport.deduped.removed === 1 ? '' : 's'} merged</span>
                        <span>·</span>
                        <span>{cleanupReport.normalized.changed} status{cleanupReport.normalized.changed === 1 ? '' : 'es'} normalized</span>
                        <button onClick={() => setCleanupReport(null)} className="ml-auto text-emerald-700 hover:underline">Dismiss</button>
                    </div>
                )}

                <div className="bg-white rounded-xl shadow-lg p-8">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <div className="bg-blue-600 p-3 rounded-lg">
                                <Briefcase className="text-white" size={24} />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-800">Application Tracker</h1>
                                <p className="text-sm text-slate-500">{jobs.length} jobs tracked</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <Filter size={16} className="text-slate-400" />
                            <select
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                                className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                            >
                                <option value="All">All Status</option>
                                {STATUS_OPTIONS.map(status => (
                                    <option key={status} value={status}>{status}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {filteredJobs.length === 0 ? (
                        <div className="text-center py-12">
                            <Briefcase size={48} className="mx-auto text-slate-300 mb-4" />
                            <p className="text-slate-500 mb-4">
                                {filterStatus === 'All' ? 'No jobs tracked yet' : `No jobs with status "${filterStatus}"`}
                            </p>
                            <button
                                onClick={onAnalyzeNew}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                            >
                                Analyze Your First Job
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {filteredJobs.map(job => (
                                <div key={job.id} className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <h3 className="text-lg font-bold text-slate-800">{job.title}</h3>
                                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[job.status]}`}>
                                                    {job.status}
                                                </span>
                                                {job.matchScore && (
                                                    <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                                                        {job.matchScore}% Match
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-sm text-slate-600 mb-2">{job.company}</p>
                                            {job.link && (
                                                <a
                                                    href={job.link}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                                                >
                                                    View Job Posting <ExternalLink size={12} />
                                                </a>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => handleDelete(job.id)}
                                            className="text-slate-400 hover:text-red-500 p-2"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </div>

                                    <div className="flex items-center gap-2 mb-3">
                                        <span className="text-xs text-slate-500">Status:</span>
                                        <select
                                            value={job.status}
                                            onChange={(e) => handleStatusChange(job.id, e.target.value)}
                                            className="px-2 py-1 border border-slate-300 rounded text-xs focus:ring-2 focus:ring-blue-500 outline-none"
                                        >
                                            {STATUS_OPTIONS.map(status => (
                                                <option key={status} value={status}>{status}</option>
                                            ))}
                                        </select>
                                        <span className="text-xs text-slate-400 ml-auto">
                                            Added {new Date(job.dateAdded).toLocaleDateString()}
                                        </span>
                                    </div>

                                    {job.analysis && (
                                        <details className="mt-3">
                                            <summary className="cursor-pointer text-sm font-medium text-blue-600 hover:text-blue-700">
                                                View Analysis
                                            </summary>
                                            <div className="mt-3 p-3 bg-slate-50 rounded-lg space-y-2 text-sm">
                                                <div>
                                                    <span className="font-medium text-slate-700">Matching Skills: </span>
                                                    <span className="text-slate-600">{job.analysis.matchingSkills.join(', ')}</span>
                                                </div>
                                                <div>
                                                    <span className="font-medium text-slate-700">Missing Skills: </span>
                                                    <span className="text-slate-600">{job.analysis.missingSkills.join(', ')}</span>
                                                </div>
                                                <button
                                                    onClick={() => onTailor(job.analysis)}
                                                    className="w-full mt-2 bg-purple-100 text-purple-700 hover:bg-purple-200 px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                                                >
                                                    <Sparkles size={16} />
                                                    Tailor Resume for this Job
                                                </button>
                                            </div>
                                        </details>
                                    )}
                                    <button
                                        onClick={() => onEdit(job)}
                                        className="w-full mt-3 bg-blue-50 text-blue-700 hover:bg-blue-100 px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                                    >
                                        <Sparkles size={16} />
                                        Open Full Analysis & Tools
                                    </button>

                                    {/* CV snapshot row — career-ops's per-listing CV pattern */}
                                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                                        {job.snapshot ? (
                                            <>
                                                <div className="flex-1 text-xs text-slate-600">
                                                    <span className="font-medium text-emerald-700">CV snapshot saved</span>
                                                    <span className="text-slate-400 ml-1">· {new Date(job.snapshot.savedAt).toLocaleDateString()}</span>
                                                </div>
                                                <button
                                                    onClick={() => handleDownloadSnapshot(job)}
                                                    className="text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 px-2 py-1.5 rounded-md flex items-center gap-1"
                                                    title="Download the exact CV you sent for this application"
                                                >
                                                    <Download size={12} /> Download
                                                </button>
                                                <button
                                                    onClick={() => handleClearSnapshot(job.id)}
                                                    className="text-xs font-medium text-slate-500 hover:bg-slate-100 px-2 py-1.5 rounded-md"
                                                    title="Remove snapshot"
                                                >
                                                    Clear
                                                </button>
                                            </>
                                        ) : (
                                            <button
                                                onClick={() => handleSnapshot(job.id)}
                                                disabled={!currentResumeData}
                                                className="w-full text-xs font-medium text-slate-700 bg-slate-50 hover:bg-slate-100 disabled:opacity-50 px-2 py-2 rounded-md flex items-center justify-center gap-2"
                                                title="Freeze the current resume as the version sent for this application"
                                            >
                                                <Camera size={12} /> Snapshot current CV for this application
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ApplicationTracker;
